'use client'

import React from 'react'
import RichText from '@/components/RichText'
import type { YouTubeEmbedBlockData } from './config'

export const YouTubeEmbed: React.FC<YouTubeEmbedBlockData> = ({ videoIdOrUrl, title }) => {
  const extractYouTubeID = (input: string): string | null => {
    // Try common URL forms first
    const urlMatch = input.match(
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/i,
    )
    if (urlMatch?.[1]) return urlMatch[1]

    // Or accept a bare 11-char ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input

    return null
  }

  const videoID = extractYouTubeID(videoIdOrUrl)
  if (!videoID) return null

  // YouTube embed parameters to control player behavior
  const embedParams = new URLSearchParams({
    rel: '0', // Completely disable related videos at the end
    modestbranding: '1', // Minimal YouTube branding
    showinfo: '0', // Don't show video title and uploader
    controls: '1', // Show player controls
    disablekb: '0', // Allow keyboard controls
    fs: '1', // Allow fullscreen
    iv_load_policy: '3', // Don't show video annotations
    autoplay: '0', // Don't autoplay
    loop: '0', // Don't loop the video
    start: '0', // Start from beginning
    end: '', // Play until the end
    cc_load_policy: '0', // Don't show closed captions by default
    hl: 'en', // Set language
    origin: typeof window !== 'undefined' ? window.location.origin : '', // Security parameter
  })

  const embedUrl = `https://www.youtube.com/embed/${videoID}?${embedParams.toString()}`

  return (
    <div className="container mx-auto px-4 py-8">
      {title && (
        <div className="mb-6 text-center">
          <div className="text-2xl font-bold text-gray-800">
            <RichText data={title} enableGutter={false} enableProse={false} />
          </div>
        </div>
      )}
      <div className="w-full h-[70vh] flex items-center justify-center">
        <div className="relative w-full h-full overflow-hidden rounded-lg shadow-lg">
          <iframe
            src={embedUrl}
            title="YouTube video"
            className="absolute inset-0 w-full h-full"
            frameBorder={0}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </div>
    </div>
  )
}

export default YouTubeEmbed
